import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  // Optimistic increments are discarded whenever an authoritative count arrives.
  // This prevents a later failed request from undoing an already reconciled count.
  #optimisticAdds = new Map();

  /** @type {number} */
  get currentCartCount() {
    return Number(this.refs.cartBubbleCount.dataset.count ?? this.refs.cartBubbleCount.textContent) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.dataset.count = String(value);
    this.refs.cartBubbleCount.textContent = value < 100 || this.hasAttribute('data-show-full-count') ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    if (!event.promise) return;

    if (event.action === 'add') {
      const quantity = event.lines.reduce((total, line) => total + (Number(line.quantity) || 0), 0);
      if (quantity > 0) {
        this.#optimisticAdds.set(event, quantity);
        this.renderCartBubble(this.currentCartCount + quantity, true, false);
      }
    }

    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount;
        if (!Number.isFinite(itemCount)) {
          this.#rollbackOptimisticAdd(event);
          return;
        }

        this.#optimisticAdds.clear();
        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        this.#rollbackOptimisticAdd(event);
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  #rollbackOptimisticAdd(event) {
    const quantity = this.#optimisticAdds.get(event);
    if (quantity === undefined) return;
    this.#optimisticAdds.delete(event);
    this.renderCartBubble(Math.max(0, this.currentCartCount - quantity), false, false);
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   * @param {boolean} [persist=true] - Only persist confirmed server counts.
   */
  renderCartBubble = async (itemCount, animate = true, persist = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    const cartLink = this.closest('.gc-header__cart');
    cartLink?.setAttribute('aria-label', itemCount > 0 ? `Cart, ${itemCount} items` : 'Cart');

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (persist) {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now(),
        })
      );
    }

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
